import React, { useState, useEffect } from 'react';
import { certificatService } from '../../../services/certificatService';
import clientService from '../../../services/clientService';
import { useTheme } from '../../../contexts/ThemeContext';
import ClientSelectDropdown from '../../common/ClientSelectDropdown';

const EditCertificatModal = ({ isOpen, certificat, onClose, onSuccess }) => {
  const { isDarkMode } = useTheme();

  const [formData, setFormData] = useState({
    pieceName: '',
    clientId: '',
    dateCertif: '',
    pieceFile: null,
    piecePath: ''
  });
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [error, setError] = useState('');
  const [searchClient, setSearchClient] = useState('');

  useEffect(() => {
    if (certificat) {
      setFormData({
        pieceName: certificat.pieceName || '',
        clientId: certificat.clientId?.toString() || '',
        dateCertif: certificat.dateCertif
          ? new Date(certificat.dateCertif).toISOString().slice(0, 16)
          : '',
        pieceFile: null,
        piecePath: certificat.piecePath || ''
      });
    }
  }, [certificat]);

  useEffect(() => {
    if (isOpen) {
      loadClients();
    }
  }, [isOpen]);

  const loadClients = async () => {
    setLoadingClients(true);
    try {
      const result = await clientService.getClients(100, 1);
      if (result.success && result.data?.list) {
        setClients(result.data.list);
      } else if (result.success && result.data?.data) {
        setClients(result.data.data);
      } else {
        setClients([]);
      }
    } catch (error) {
      setError('Erreur lors du chargement des clients');
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  };

  const getFilteredClients = () => {
    if (!searchClient) return clients;
    return clients.filter(client =>
      (client.raisonSocial || '').toLowerCase().includes(searchClient.toLowerCase()) ||
      `${client.nom || ''} ${client.prenom || ''}`.toLowerCase().includes(searchClient.toLowerCase()) ||
      (client.matriculeFiscal || '').toLowerCase().includes(searchClient.toLowerCase())
    );
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      pieceFile: file
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (!formData.pieceName.trim()) throw new Error('Le nom du certificat est obligatoire');
      if (!formData.clientId) throw new Error('Le client est obligatoire');

      const formDataPayload = new FormData();
      formDataPayload.append('Id', certificat.id);
      formDataPayload.append('ClientId', parseInt(formData.clientId, 10));
      formDataPayload.append('DateCertif', formData.dateCertif ? new Date(formData.dateCertif).toISOString() : new Date().toISOString());
      if (formData.pieceFile) {
        formDataPayload.append('Piece', formData.pieceFile, formData.pieceFile.name);
      }
      formDataPayload.append('PieceName', formData.pieceName.trim());
      if (formData.piecePath && formData.piecePath.trim()) {
        formDataPayload.append('PiecePath', formData.piecePath.trim());
      }

      const result = await certificatService.updateCertificat(certificat.id, formDataPayload);

      if (result.success) {
        onSuccess();
        resetForm();
      } else {
        setError(result.message || 'Erreur lors de la mise à jour du certificat');
      }
    } catch (error) {
      setError(error.message || 'Erreur lors de la mise à jour du certificat');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSearchClient('');
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen || !certificat) return null;

  const selectedClient = clients.find(c => c.id === parseInt(formData.clientId));

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col`}>
        {/* Header */}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-r from-primary-600 to-primary-700'} px-6 py-4 flex-shrink-0`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`${isDarkMode ? 'bg-white bg-opacity-10' : 'bg-white bg-opacity-20'} w-10 h-10 rounded-xl flex items-center justify-center mr-3`}>
                <i className="fas fa-edit text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Modifier le Certificat</h2>
                <p className={`${isDarkMode ? 'text-gray-300' : 'text-primary-100'} text-sm`}>
                  {certificat.pieceName || `Certificat #${certificat.id}`}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="p-6">
            {/* Error Message */}
            {error && (
              <div className={`mb-6 p-4 rounded-xl border ${isDarkMode ? 'bg-gray-900 border-gray-800 text-gray-200' : 'bg-red-50 border-red-200 text-red-700'}`}>
                <div className="flex items-center">
                  <i className="fas fa-exclamation-circle mr-3 text-red-500"></i>
                  <div>
                    <div className="font-medium">Erreur de validation</div>
                    <div className="text-sm">{error}</div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className={`${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'} p-2 rounded-lg mr-3`}>
                    <i className={`fas fa-info-circle ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Informations du certificat</h3>
                  <span className="ml-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">* Champs obligatoires</span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-tag mr-2 text-gray-400"></i>
                      Nom du certificat *
                    </label>
                    <input
                      type="text"
                      name="pieceName"
                      value={formData.pieceName}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'}`}
                      placeholder="Ex: Certificat visite extincteurs 2025"
                      required
                    />
                    {!formData.pieceName && (
                      <p className="text-xs text-red-600 mt-1">Ce champ est obligatoire</p>
                    )}
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-calendar mr-2 text-gray-400"></i>
                      Date de certification
                    </label>
                    <input
                      type="datetime-local"
                      name="dateCertif"
                      value={formData.dateCertif}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'}`}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-file-upload mr-2 text-gray-400"></i>
                      Remplacer le fichier du certificat (optionnel)
                    </label>
                    <div className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-primary-400 transition-colors ${isDarkMode ? 'border-gray-600' : 'border-gray-300'}`}>
                      <input
                        type="file"
                        name="pieceFile"
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      />
                      <label htmlFor="file-upload" className="cursor-pointer">
                        {formData.pieceFile ? (
                          <div className="flex items-center justify-center">
                            <i className="fas fa-file-check text-green-500 text-2xl mr-3"></i>
                            <div>
                              <p className="text-green-600 font-medium">{formData.pieceFile.name}</p>
                              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                {(formData.pieceFile.size / 1024 / 1024).toFixed(2)} MB - Nouveau fichier
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <i className="fas fa-cloud-upload-alt text-gray-400 text-3xl mb-3"></i>
                            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>Cliquez pour sélectionner un nouveau fichier</p>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>PDF, DOC, DOCX, JPG, PNG (Max 10MB)</p>
                            <p className="text-xs text-blue-600 mt-2">
                              <i className="fas fa-info-circle mr-1"></i>
                              Laisser vide pour conserver le fichier actuel
                            </p>
                          </div>
                        )}
                      </label>
                    </div>
                  </div>

                  <div className="md:col-span-2">
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-folder mr-2 text-gray-400"></i>
                      Chemin du fichier (optionnel)
                    </label>
                    <input
                      type="text"
                      name="piecePath"
                      value={formData.piecePath}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900 placeholder-gray-500'}`}
                      placeholder="Chemin de stockage personnalisé"
                    />
                  </div>
                </div>
              </div>

              {/* Client Selection with Search */}
              <div className={`${isDarkMode ? 'bg-gray-900 border border-blue-900' : 'bg-blue-50 border border-blue-200'} rounded-xl p-6`}>
                <div className="flex items-center mb-4">
                  <div className={`${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'} p-2 rounded-lg mr-3`}>
                    <i className={`fas fa-building ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Client</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-user mr-2 text-gray-400"></i>
                      Sélectionner un client *
                    </label>
                    <ClientSelectDropdown
                      value={formData.clientId}
                      onChange={val => setFormData(prev => ({ ...prev, clientId: val }))}
                      options={clients}
                      loading={loadingClients}
                      disabled={loadingClients}
                      isDarkMode={isDarkMode}
                      placeholder="Sélectionner un client"
                      required={true}
                    />
                    {searchClient && (
                      <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {getFilteredClients().length} résultat(s) trouvé(s)
                      </div>
                    )}
                  </div>

                  {/* Selected Client Preview */}
                  {selectedClient && (
                    <div className={`${isDarkMode ? 'bg-gray-800 border-blue-900' : 'bg-white border-blue-200'} rounded-lg p-4 border`}>
                      <div className="flex items-center mb-3">
                        <div className={`${isDarkMode ? 'bg-blue-900' : 'bg-blue-100'} w-10 h-10 rounded-lg flex items-center justify-center mr-3`}>
                          <i className={`fas fa-building ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}></i>
                        </div>
                        <div>
                          <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Client sélectionné</h4>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Informations du client</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-blue-700 font-medium">Raison sociale: </span>
                          <span className={isDarkMode ? 'text-blue-300' : 'text-blue-900'}>
                            {selectedClient.raisonSocial || `${selectedClient.nom || ''} ${selectedClient.prenom || ''}`.trim() || 'N/A'}
                          </span>
                        </div>
                        {selectedClient.matriculeFiscal && (
                          <div>
                            <span className="text-blue-700 font-medium">Matricule: </span>
                            <span className={isDarkMode ? 'text-blue-300' : 'text-blue-900'}>{selectedClient.matriculeFiscal}</span>
                          </div>
                        )}
                        {selectedClient.tel && (
                          <div>
                            <span className="text-blue-700 font-medium">Téléphone: </span>
                            <span className={isDarkMode ? 'text-blue-300' : 'text-blue-900'}>{selectedClient.tel}</span>
                          </div>
                        )}
                        {selectedClient.login && (
                          <div>
                            <span className="text-blue-700 font-medium">Login: </span>
                            <span className={isDarkMode ? 'text-blue-300' : 'text-blue-900'}>{selectedClient.login}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Changes Preview */}
              {formData.pieceName && formData.clientId && (
                <div className={`${isDarkMode ? 'bg-gray-900 border border-primary-900' : 'bg-primary-50 border border-primary-200'} rounded-xl p-6`}>
                  <div className="flex items-center mb-4">
                    <div className={`${isDarkMode ? 'bg-primary-900' : 'bg-primary-100'} p-2 rounded-lg mr-3`}>
                      <i className={`fas fa-eye ${isDarkMode ? 'text-primary-300' : 'text-primary-600'}`}></i>
                    </div>
                    <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Aperçu des modifications</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Before */}
                    <div>
                      <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Avant</h4>
                      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-gray-100'} rounded-lg p-4`}>
                        <div className="flex items-center">
                          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} w-10 h-10 rounded-lg flex items-center justify-center mr-3`}>
                            <i className={`fas fa-certificate ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} text-sm`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {certificat.pieceName || 'Document sans nom'}
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{certificat.client?.raisonSocial || 'Client inconnu'}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* After */}
                    <div>
                      <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Après</h4>
                      <div className={`${isDarkMode ? 'bg-gray-800 border-primary-900' : 'bg-white border-primary-200'} rounded-lg p-4 border`}>
                        <div className="flex items-center">
                          <div className={`${isDarkMode ? 'bg-primary-900' : 'bg-primary-100'} w-10 h-10 rounded-lg flex items-center justify-center mr-3`}>
                            <i className={`fas fa-certificate ${isDarkMode ? 'text-primary-300' : 'text-primary-600'} text-sm`}></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                              {formData.pieceName || 'Document sans nom'}
                            </p>
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                              {selectedClient?.raisonSocial || `${selectedClient?.nom || ''} ${selectedClient?.prenom || ''}`.trim() || 'Client non sélectionné'}
                            </p>
                            {formData.pieceFile && (
                              <p className="text-xs text-green-600 mt-1">
                                <i className="fas fa-upload mr-1"></i>
                                Nouveau fichier: {formData.pieceFile.name}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className={`border-t px-6 py-4 flex-shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className={`px-6 py-3 rounded-lg transition-colors font-medium border ${isDarkMode ? 'text-gray-200 bg-gray-800 border-gray-700 hover:bg-gray-700' : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'}`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || loadingClients || !formData.pieceName.trim() || !formData.clientId}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                loading || loadingClients || !formData.pieceName.trim() || !formData.clientId
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : isDarkMode
                  ? 'bg-primary-700 hover:bg-primary-800 text-white shadow-lg hover:shadow-xl'
                  : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Sauvegarde...
                </div>
              ) : (
                <div className="flex items-center">
                  <i className="fas fa-save mr-2"></i>
                  Sauvegarder les modifications
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditCertificatModal;